import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { supabase } from "@/integrations/supabase/client";
import { formatDomainDisplay } from "@/utils/domainDisplay";
import { useToast } from "@/hooks/use-toast";
import { deduplicateDomains } from "@/utils/domainDeduplication";

interface Domain {
  id: string;
  name: string;
  symbol?: string;
  description?: string;
  channel_id?: string;
  created_at?: string;
}

interface Subdomain {
  id: string;
  name: string;
  description: string;
  domain_id: string;
}

const CallForKnowledgeFunctionalArea = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>("");
  const [domains, setDomains] = useState<Domain[]>([]);
  const [subdomains, setSubdomains] = useState<Subdomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubdomains, setLoadingSubdomains] = useState(false);

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    if (selectedDomain) {
      fetchSubdomains(selectedDomain);
    } else {
      setSubdomains([]);
      setSelectedSubdomain("");
    }
  }, [selectedDomain]);

  const fetchDomains = async () => {
    try {
      const { data, error } = await supabase
        .from('domains')
        .select('*')
        .order('name');

      if (error) throw error;
      setDomains(deduplicateDomains(data || []));
    } catch (error: any) {
      toast({
        title: "Error loading domains",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubdomains = async (domainId: string) => {
    setLoadingSubdomains(true);
    try {
      const { data, error } = await supabase
        .from('subdomains')
        .select('*')
        .eq('domain_id', domainId)
        .order('name');

      if (error) throw error;
      setSubdomains(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading subdomains",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoadingSubdomains(false);
    }
  };

  const handleContinue = () => {
    if (!selectedDomain || !selectedSubdomain) return;
    navigate("/call-for-knowledge/bounty", {
      state: { selectedDomain, selectedSubdomain }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex flex-col items-center p-4 pb-12">
      {/* Logo */}
      <div className="mt-8 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/engage")}
        />
      </div>

      <Card className="w-full max-w-3xl border border-gray-200">
        <CardContent className="p-6">
          <h2 className="text-2xl font-semibold text-center mb-6">Select Knowledge Domain</h2>
          
          {loading ? (
            <p className="text-center text-gray-500">Loading domains...</p>
          ) : (
            <>
              <RadioGroup 
                value={selectedDomain} 
                onValueChange={setSelectedDomain}
                className="space-y-4 mb-6"
              >
                {domains.map((domain) => (
                  <div 
                    key={domain.id} 
                    className="flex items-start space-x-2 p-2 rounded-md hover:bg-gray-50"
                  >
                    <RadioGroupItem value={domain.id} id={domain.id} className="mt-1" />
                    <div className="flex-1">
                <Label 
                  htmlFor={domain.id} 
                  className="font-medium cursor-pointer flex items-center"
                >
                  {formatDomainDisplay(domain)}
                </Label>
                      <p className="text-gray-600 text-sm ml-2">{domain.description}</p>
                    </div>
                  </div>
                ))}
              </RadioGroup>

              {selectedDomain && (
                <div className="mt-8 pt-6 border-t">
                  <h3 className="text-xl font-semibold mb-4">Select Subdomain</h3>
                  {loadingSubdomains ? (
                    <p className="text-center text-gray-500">Loading subdomains...</p>
                  ) : subdomains.length > 0 ? (
                    <RadioGroup 
                      value={selectedSubdomain} 
                      onValueChange={setSelectedSubdomain}
                      className="space-y-4"
                    >
                      {subdomains.map((subdomain) => (
                        <div 
                          key={subdomain.id} 
                          className="flex items-start space-x-2 p-2 rounded-md hover:bg-gray-50"
                        >
                          <RadioGroupItem value={subdomain.id} id={subdomain.id} className="mt-1" />
                          <div className="flex-1">
                            <Label 
                              htmlFor={subdomain.id} 
                              className="font-medium cursor-pointer"
                            >
                              {subdomain.name}
                            </Label>
                            {subdomain.description && (
                              <p className="text-gray-600 text-sm ml-2">{subdomain.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  ) : (
                    <p className="text-center text-gray-500 py-4">No subdomains available for this domain</p>
                  )}
                </div>
              )}
            </>
          )}
          
          <div className="flex justify-between mt-8">
            <Button 
              variant="outline" 
              onClick={() => navigate("/call-for-knowledge")}
              className="border-gray-300"
            >
              Back
            </Button>
            <Button 
              onClick={handleContinue}
              disabled={!selectedDomain || !selectedSubdomain}
              className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default CallForKnowledgeFunctionalArea;
